import { useAnswerHistory, useDebounce } from "@hooks";
import { ChevronRight } from "@mui/icons-material";
import { Button } from "@mui/material";
import { buildChangeAssistantPrompt } from "@utils";
import { isEqual } from "lodash";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { AssistantMultiStepFormStepErrorBoundary } from "src/components/ErrorBoundary";
import { ElementMapper } from "src/components/Form/ElementMapper";
import { TextFieldEditorHandle } from "src/components/Form/Elements/TextField";
import { StepHeader } from "src/components/Form/StepHeader";
import {
  getAnswer,
  getAnswerFromDocumentVersion,
  getContextConfig,
  runTransformer,
} from "src/components/Form/utils";
import { useUpdateAssistantProcessMutation } from "src/hooks/queries/assistant-process-mutations";
import {
  useDataKeyApplyListener,
  usePromptInjection,
} from "src/hooks/useToolActions";
import { useStore } from "src/stores";
import { isStepComplete } from "src/utils/step";
import {
  AssistantMultiStepFormAnswer,
  AssistantProcess,
  Device,
  Document,
  DocumentVersion,
  RoadmapTasks,
  User,
} from "../../stores/models";
import { StepValue, TemplateConfig, TemplateQuestion } from "../../types";

type Props = {
  templateKey: string;
  step: TemplateQuestion;
  documentVersion?: DocumentVersion;
  nextStep: () => void;
  documents: Document[];
  device: Device;
  assistantProcess: AssistantProcess;
  user: User;
  templatesConfig: TemplateConfig;
  tasks: RoadmapTasks;
};

const normalizeAssistantStepState = (
  state: AssistantMultiStepFormAnswer | null | undefined
): AssistantMultiStepFormAnswer | null => {
  if (!state) {
    return null;
  }

  return JSON.parse(JSON.stringify(state));
};

const isSameAssistantStepState = (
  first: AssistantMultiStepFormAnswer | null | undefined,
  second: AssistantMultiStepFormAnswer | null | undefined
) =>
  isEqual(
    normalizeAssistantStepState(first),
    normalizeAssistantStepState(second)
  );

export const AssistantMultiStepFormStep = observer(
  ({
    templateKey,
    documentVersion,
    step,
    nextStep,
    documents,
    device,
    assistantProcess,
    user,
    templatesConfig,
    tasks,
  }: Props) => {
    const { orgId = "", deviceId = "" } = useParams();
    const { mutate: updateAssistantProcess } =
      useUpdateAssistantProcessMutation(orgId, deviceId);
    const textFieldEditorRef = useRef<TextFieldEditorHandle>(null);
    const isAnswerStringElement =
      step.element.type !== "answerItemsElement" &&
      step.element.type !== "fileUpload";

    const defaultAnswer = {
      answer: "",
      answerFileId: undefined,
      answerItems: undefined,
    };

    const { injectPrompt } = usePromptInjection();

    const savedChange = assistantProcess.state.changes.find(
      (change) => change.dataKeyId === step.id
    );

    const { agentStore, uiStore } = useStore();

    const savedPreviousAnswer: StepValue | undefined = useMemo(() => {
      if (documentVersion) {
        const answerFromExistingDocument = getAnswerFromDocumentVersion(
          step.element,
          documentVersion
        );
        if (answerFromExistingDocument) {
          return {
            answer: answerFromExistingDocument.answer,
            answerFileId: answerFromExistingDocument.answerFileId,
            answerItems: answerFromExistingDocument.answerItems,
          };
        }
      }

      return undefined;
    }, [savedChange?.previousAnswer, step, documentVersion, documents]);

    const previousAnswer = savedChange?.previousAnswer || savedPreviousAnswer;

    const defaultAcceptedAnswer = useMemo(() => {
      return getAnswer({
        step,
        documentVersion,
        documents,
        templatesConfig,
      });
    }, [step, documentVersion, documents, templatesConfig]);

    const defaultAnswerIsNotEmpty = useMemo(() => {
      return !isEqual(defaultAcceptedAnswer, {
        answer: undefined,
        answerFileId: undefined,
        answerItems: undefined,
      });
    }, [defaultAcceptedAnswer]);

    // For the new answer we can also run the transformer and all other logic we use for new answers
    const newAnswer =
      savedChange?.acceptedAnswer ||
      previousAnswer ||
      (defaultAnswerIsNotEmpty
        ? getAnswer({
            step,
            documentVersion,
            documents,
            templatesConfig,
          })
        : undefined);

    const savedAssistantStepState = useMemo<AssistantMultiStepFormAnswer | null>(
      () =>
        savedChange
          ? {
              dataKeyId: step.id,
              templateKey,
              previousAnswer,
              acceptedAnswer: newAnswer,
              suggestion: savedChange.suggestion || undefined,
            }
          : null,
      [newAnswer, previousAnswer, savedChange, step.id, templateKey]
    );

    const [assistantStepState, setAssistantStepState] =
      useState<AssistantMultiStepFormAnswer>({
        dataKeyId: step.id,
        templateKey: templateKey,
        previousAnswer,
        acceptedAnswer: newAnswer,
        suggestion: savedChange?.suggestion || undefined,
      });
    const lastSyncedSavedStepStateRef =
      useRef<AssistantMultiStepFormAnswer | null>(savedAssistantStepState);
    const lastSubmittedStepStateRef =
      useRef<AssistantMultiStepFormAnswer | null>(null);

    useEffect(() => {
      if (!savedAssistantStepState) {
        lastSyncedSavedStepStateRef.current = null;
        return;
      }

      if (
        isSameAssistantStepState(
          lastSyncedSavedStepStateRef.current,
          savedAssistantStepState
        )
      ) {
        return;
      }

      const savedChangeCameFromThisStep =
        !!lastSubmittedStepStateRef.current &&
        isSameAssistantStepState(
          lastSubmittedStepStateRef.current,
          savedAssistantStepState
        );

      lastSyncedSavedStepStateRef.current = savedAssistantStepState;
      if (savedChangeCameFromThisStep) {
        lastSubmittedStepStateRef.current = null;
      }

      setAssistantStepState((currentState) => {
        if (
          savedChangeCameFromThisStep &&
          !isSameAssistantStepState(currentState, savedAssistantStepState)
        ) {
          return currentState;
        }

        return isSameAssistantStepState(currentState, savedAssistantStepState)
          ? currentState
          : savedAssistantStepState;
      });
    }, [savedAssistantStepState]);

    const stepIsComplete: boolean = useMemo(() => {
      return (
        !!assistantStepState.acceptedAnswer &&
        isStepComplete(step, assistantStepState.acceptedAnswer)
      );
    }, [assistantStepState.acceptedAnswer, step]);

    const stepValue =
      assistantStepState.acceptedAnswer ||
      assistantStepState.previousAnswer ||
      defaultAnswer;

    const saveAssistantProcessStep = (step: AssistantMultiStepFormAnswer) => {
      lastSubmittedStepStateRef.current = step;
      updateAssistantProcess({
        processId: assistantProcess.id,
        body: {
          state: {
            ...assistantProcess.state,
            changes: [
              ...assistantProcess.state.changes.filter(
                (change) => change.dataKeyId !== step.dataKeyId
              ),
              step,
            ],
          },
        },
      });
    };

    const { debounce, cancelDebounce } = useDebounce(
      2000,
      (step: AssistantMultiStepFormAnswer) => {
        if (isAnswerStringElement) {
          saveAssistantProcessStep(step);
        }
      }
    );

    const handleNextStep = () => {
      cancelDebounce();

      if (step.element.type === "textField") {
        const markdown =
          textFieldEditorRef.current?.getMarkdownContent() ??
          assistantStepState.acceptedAnswer?.answer ??
          "";
        const updatedState = {
          ...assistantStepState,
          acceptedAnswer: {
            answer: markdown,
            answerFileId: undefined,
            answerItems: undefined,
          },
        };
        saveAssistantProcessStep(updatedState);
      } else {
        saveAssistantProcessStep(assistantStepState);
      }

      nextStep();
    };

    const handleChange = (value: StepValue) => {
      if (isEqual(value, defaultAnswer)) {
        return;
      }
      setAssistantStepState((prev) => {
        const newState = {
          ...prev,
          acceptedAnswer: value,
        };
        debounce(newState);
        return newState;
      });
    };

    const [updateHistory, undo, redo] = useAnswerHistory({
      elementId: step.id,
    });

    /**
     * In order to keep the history of the answer, we need to update the history when the answer changes
     */
    useEffect(() => {
      if (isAnswerStringElement) {
        updateHistory(assistantStepState.acceptedAnswer?.answer);
      }
    }, [assistantStepState.acceptedAnswer]);

    const handleUndo = () => {
      const newAnswer = undo();
      if (newAnswer) {
        handleChange({ answer: newAnswer });
      }
    };

    const handleRedo = () => {
      const newAnswer = redo();
      if (newAnswer) {
        handleChange({ answer: newAnswer });
      }
    };

    // Listen for tool actions that want to apply values to this step
    const handleApplyFromTool = (value: string) => {
      handleChange({ answer: value });
    };

    useDataKeyApplyListener(step.id, handleApplyFromTool, {});

    const handleAutoSuggest = async () => {
      const contextConfig = getContextConfig(
        step,
        documents,
        templatesConfig,
        device
      );
      const fullPrompt = buildChangeAssistantPrompt(
        step.id,
        assistantProcess,
        templatesConfig,
        contextConfig,
        device
      );

      const preview = `Auto-suggest for: ${step.element.options.label}`;

      agentStore.navigateToThread(null);
      uiStore.setIsRightPanelOpen(true);

      injectPrompt({
        prompt: fullPrompt,
        preview: preview,
        source: `auto-suggest-${step.id}`,
        submit: false,
        replaceInput: true,
        contextConfig: contextConfig,
        device: device,
        documents: documents,
        user: user,
        deviceId: deviceId,
        templatesConfig: templatesConfig,
      });
    };

    const handleForceRunTransformer = () => {
      if (!documentVersion) {
        return;
      }
      const newAnswer = runTransformer({
        step,
        documentVersion,
        documents,
        templatesConfig,
      });
      handleChange({ answer: newAnswer, answerFileId: undefined });
    };

    const contextConfig = getContextConfig(
      step,
      documents,
      templatesConfig,
      device
    );

    // Single panel elements with no suggestion
    return (
      <AssistantMultiStepFormStepErrorBoundary>
        <div key={step.id} className={`flex flex-1 flex-col gap-y-2`}>
          <StepHeader templateElement={step} documents={documents} />

          <div className={`flex flex-1 flex-col gap-y-4 w-full`}>
            <ElementMapper
              step={step}
              templatesConfig={templatesConfig}
              stepValue={stepValue}
              documentVersion={documentVersion}
              onChange={(_, value) => handleChange(value)}
              onUndo={handleUndo}
              onRedo={handleRedo}
              onReRunTransformer={handleForceRunTransformer}
              contextConfig={contextConfig}
              deviceId={deviceId}
              tasks={tasks}
              onAutoSuggest={handleAutoSuggest}
              textFieldEditorRef={textFieldEditorRef}
            />
            <div className="self-end">
              <Button
                disabled={!stepIsComplete}
                onClick={handleNextStep}
                variant="contained"
                endIcon={<ChevronRight />}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      </AssistantMultiStepFormStepErrorBoundary>
    );
  }
);
